import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mic, ImagePlus } from 'lucide-react';
import { ImageUploader } from '../components/ImageUploader';
import { useWallet } from '../context/WalletContext';
import { useRequestPrice } from '../hooks/useRequestPrice';
import { useProfileSettings } from '../hooks/useProfileSettings';
import { useCurrency } from '../hooks/useCurrency';
import { useAuth } from '../hooks/useAuth';
import { addDoc, collection } from 'firebase/firestore';
import { db, REQUESTS_COLLECTION } from '../config/firebase';
import { useExtensionPrice } from '../hooks/useExtensionPrice';
import { PageHeader } from '../components/PageHeader';
import { useServicesStatus } from '../hooks/useServicesStatus';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { prepareRequestData } from '../utils/migrationUtils';

const MAX_IMAGES = 4;

interface RequestFormData {
  title: string;
  details: string;
  amount: string;
  country: string;
  city: string;
  images: string[];
  audioUrl?: string;
  isPublic: boolean;
  mediaType: 'images' | 'audio';
  link: string;
  hasFixedAmount: boolean;
  isLinkPublic: boolean;
  minDonationAmount: string;
  subscriptionEnabled: boolean;
  displayDuration: number;  // Durée d'affichage en jours
}

export function CreateRequest() {
  const navigate = useNavigate();
  const { balance, updateBalance } = useWallet();
  const { price } = useRequestPrice();
  const { settings } = useProfileSettings();
  const currency = useCurrency();
  const { user } = useAuth();
  const { price: extensionPrice } = useExtensionPrice();
  const { status: servicesStatus } = useServicesStatus();
  const { isPlaying } = useAudioPlayer();
  const [formData, setFormData] = useState<RequestFormData>({
    title: '',
    details: '',
    amount: '',
    country: 'Cameroun',
    city: '',
    images: [],
    audioUrl: undefined,
    isPublic: true,
    mediaType: 'images',
    link: '',
    hasFixedAmount: false,
    isLinkPublic: true,
    minDonationAmount: '100',
    subscriptionEnabled: false,
    displayDuration: 30,
  });

  const audioInputRef = useRef<HTMLInputElement>(null);
  // Utiliser un élément audio séparé pour la prévisualisation
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  // Préserver l'état de lecture de l'audio principal
  useEffect(() => {
    // Sauvegarder l'état de lecture actuel au montage
    if (isPlaying) {
      localStorage.setItem('audioWasPlaying', 'true');
    }
    
    return () => {
      // Rien à faire au démontage, l'état est déjà sauvegardé
    };
  }, [isPlaying]);

  useEffect(() => {
    // Créer un élément audio séparé pour la prévisualisation
    if (!previewAudioRef.current) {
      previewAudioRef.current = new Audio();
      
      // Empêcher l'élément audio de prévisualisation d'interférer avec l'audio principal
      previewAudioRef.current.onplay = (e) => {
        // Empêcher la propagation de l'événement pour éviter d'interférer avec l'audio principal
        e.stopPropagation();
      };
      
      previewAudioRef.current.onpause = (e) => {
        // Empêcher la propagation de l'événement pour éviter d'interférer avec l'audio principal
        e.stopPropagation();
      };
    }
    
    // Nettoyer l'audio de prévisualisation lors du démontage
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.src = '';
        previewAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      country: settings.country,
      city: settings.city
    }));
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      alert("Vous devez être connecté pour créer une demande");
      return;
    }

    if (user.balance < 1) {
      alert("Vous n'avez pas assez de crédits pour créer une demande");
      return;
    }

    try {
      if (!servicesStatus?.withdrawals.active) {
        alert(servicesStatus?.withdrawals.message || "Le service est temporairement indisponible");
        return;
      }

      let finalAudioUrl = formData.audioUrl;
      let audioTrimInfo = null;
      const trimInfoStr = localStorage.getItem('audioTrimInfo');
      
      if (trimInfoStr && formData.mediaType === 'audio') {
        audioTrimInfo = JSON.parse(trimInfoStr);
        finalAudioUrl = audioTrimInfo.originalUrl;
      }

      const success = await updateBalance(-price);
      if (!success) {
        alert("Erreur lors du paiement de la demande");
        return;
      }
      
      const initialRequestData = {
        title: formData.title,
        details: formData.details,
        amount: Number(formData.amount) || 0,
        country: settings.country,
        city: settings.city,
        images: formData.images || [],
        audioUrl: finalAudioUrl || null,
        audioTrimInfo: audioTrimInfo,
        isPublic: formData.isPublic,
        link: formData.link || '',
        hasFixedAmount: formData.hasFixedAmount,
        isLinkPublic: formData.isLinkPublic,
        minDonationAmount: Number(formData.minDonationAmount),
        userId: user.id,
        creatorName: settings.username || 'Anonyme',
        creatorCity: settings.city || '',
        creatorCountry: settings.country || '',
        creatorProfilePicture: settings.profilePicture || null,
        createdAt: new Date(),
        donations: [],
        likes: 0,
        hasDonations: false,
        mediaType: formData.mediaType,
        subscriptionEnabled: formData.subscriptionEnabled,
        displayDuration: formData.displayDuration,
        displayEndDate: new Date(Date.now() + (formData.displayDuration * 24 * 60 * 60 * 1000)),
        isExpired: false
      };

      const requestData = prepareRequestData(initialRequestData);
      
      if (formData.mediaType === 'audio') {
        localStorage.removeItem('trimmedAudio');
        localStorage.removeItem('audioTrimInfo');
        
        // Arrêter l'audio de prévisualisation
        if (previewAudioRef.current) {
          previewAudioRef.current.pause();
          previewAudioRef.current.src = '';
        }
      }

      const docRef = await addDoc(collection(db, REQUESTS_COLLECTION), requestData);
      const newRequestWithId = { ...requestData, id: docRef.id };
      
      if (formData.isPublic) {
        navigate('/2dr', { state: { newRequest: newRequestWithId } });
      } else {
        navigate('/profil', { state: { newRequest: newRequestWithId } });
      }
    } catch (error) {
      console.error('Failed to create request:', error);
      alert(error instanceof Error ? error.message : 'Une erreur est survenue lors de la création de la demande.');
    }
  };

  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const audioUrl = URL.createObjectURL(file);
      const audio = new Audio(audioUrl);
      
      // Attendre que les métadonnées soient chargées pour vérifier la durée
      await new Promise((resolve, reject) => {
        audio.addEventListener('loadedmetadata', resolve);
        audio.addEventListener('error', reject);
      });

      if (audio.duration > 60) {
        alert("L'audio ne doit pas dépasser 60 secondes. Veuillez choisir un fichier plus court.");
        if (audioInputRef.current) {
          audioInputRef.current.value = '';
        }
        return;
      }

      setFormData(prev => ({
        ...prev,
        audioUrl: audioUrl,
        images: []
      }));
    } catch (error) {
      console.error('Erreur lors du chargement de l\'audio:', error);
      alert("Erreur lors du chargement de l'audio. Veuillez réessayer.");
      if (audioInputRef.current) {
        audioInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const switchMediaType = (type: 'images' | 'audio') => {
    setFormData(prev => ({
      ...prev,
      mediaType: type,
      images: type === 'audio' ? [] : prev.images,
      audioUrl: type === 'images' ? undefined : prev.audioUrl
    }));
    
    // Arrêter l'audio de prévisualisation si on passe aux images
    if (type === 'images' && previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.src = '';
    }
  };

  return (
    <div className="min-h-screen pb-20">
      <PageHeader title="Créez, Recevez, Donnez ! " />
      <div className="container mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-yellow-500/10 p-4 rounded-xl">
            <p className="text-yellow-400 text-sm">
              La création d'une publication coûte {price} {currency}. 
              Votre solde actuel : {balance} {currency}
            </p>
          </div>

          <div>
            <label className="block text-white mb-2">Titre</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value.slice(0, 50) }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
              required
              maxLength={50}
            />
            <p className="text-sm text-gray-400 mt-1">{formData.title.length}/50 caractères</p>
          </div>

          <div>
            <label className="block text-white mb-2">Détails</label>
            <textarea
              value={formData.details}
              onChange={e => setFormData(prev => ({ ...prev, details: e.target.value.slice(0, 1500) }))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50 min-h-[150px]"
              required
              maxLength={1500}
            />
            <p className="text-sm text-gray-400 mt-1">{formData.details.length}/1500 caractères</p>
          </div>

          <div className="flex items-center justify-between mb-4">
            <span className="text-white">Définir un montant souhaité</span>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ 
                ...prev, 
                hasFixedAmount: !prev.hasFixedAmount,
                amount: !prev.hasFixedAmount ? prev.amount : ''
              }))}
              className={`w-12 h-6 rounded-full transition-colors relative ${
                formData.hasFixedAmount ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                formData.hasFixedAmount ? 'right-1' : 'left-1'
              }`} />
            </button>
          </div>

          {formData.hasFixedAmount && (
            <div className="space-y-4">
              <div>
                <label className="block text-white mb-2">Montant souhaité ({currency})</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.amount}
                  onChange={e => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData(prev => ({ ...prev, amount: value ? value : '' }));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
                  placeholder="Entrez un montant"
                  required={formData.hasFixedAmount}
                />
              </div>

              <div>
                <label className="block text-white mb-2">Montant minimum ({currency})</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.minDonationAmount}
                  onChange={e => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setFormData(prev => ({ ...prev, minDonationAmount: value ? value : '100' }));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
                  placeholder="Montant minimum par donation"
                  required
                />
                <p className="text-sm text-gray-400 mt-1">Le minimum que les donateurs (trices) devront respecter</p>
              </div>
            </div>
          )}

          <div className="flex gap-4 mb-6">
            <button
              type="button"
              onClick={() => switchMediaType('images')}
              className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                formData.mediaType === 'images'
                  ? 'bg-green-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <ImagePlus className="w-5 h-5" />
              <span>Images</span>
            </button>
            <button
              type="button"
              onClick={() => switchMediaType('audio')}
              className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                formData.mediaType === 'audio'
                  ? 'bg-green-500 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Mic className="w-5 h-5" />
              <span>Audio</span>
            </button>
          </div>

          <div>
            <label className="block text-white mb-2">
              {formData.mediaType === 'images' ? 'Images' : 'Audio'}
            </label>
            
            {formData.mediaType === 'images' ? (
              <div className="grid grid-cols-3 gap-4 mb-4">
                {formData.images.length === MAX_IMAGES && (
                  <p className="text-yellow-400 text-sm col-span-3">
                    Limite de {MAX_IMAGES} images atteinte
                  </p>
                )}
                {formData.images.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
                {formData.images.length < MAX_IMAGES && (
                  <ImageUploader
                    onImageCropped={(croppedImage) => {
                      setFormData(prev => ({
                        ...prev,
                        images: [...prev.images, croppedImage]
                      }));
                    }}
                    aspectRatio={16/9}
                  />
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {formData.audioUrl ? (
                  <div className="relative bg-white/5 p-4 rounded-xl">
                    <audio
                      src={formData.audioUrl}
                      controls
                      className="w-full"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, audioUrl: undefined }));
                        if (audioInputRef.current) {
                          audioInputRef.current.value = '';
                        }
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <label className="w-full h-24 bg-white/5 border border-white/10 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-white/10 transition-colors">
                    <Mic className="w-6 h-6 text-gray-400 mb-2" />
                    <span className="text-gray-400 text-sm">Cliquez pour ajouter un audio</span>
                    <input
                      ref={audioInputRef}
                      type="file"
                      accept="audio/*"
                      onChange={handleAudioUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-white">Mode public</span>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
              className={`w-12 h-6 rounded-full transition-colors relative ${
                formData.isPublic ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <div
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  formData.isPublic ? 'right-1' : 'left-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-white mb-2">Lien externe (optionnel)</label>
            <input
              type="url"
              value={formData.link}
              onChange={e => setFormData(prev => ({ ...prev, link: e.target.value }))}
              placeholder="https://..."
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500/50"
            />
            {formData.link && (
              <div className="flex items-center justify-between mt-2">
                <span className="text-white">Lien accessible à tous</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    isLinkPublic: !prev.isLinkPublic 
                  }))}
                  className={`w-12 h-6 rounded-full transition-colors relative ${
                    formData.isLinkPublic ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    formData.isLinkPublic ? 'right-1' : 'left-1'
                  }`} />
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2 mt-4">
            {!formData.hasFixedAmount && (
              <div className="space-y-4">
                <p className="text-white">Durée d'affichage : {formData.displayDuration} jours</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (balance >= extensionPrice) {
                        setFormData(prev => ({
                          ...prev,
                          displayDuration: prev.displayDuration + 30
                        }));
                      } else {
                        alert(`Solde insuffisant pour ajouter 30 jours (${extensionPrice} {currency} requis)`);
                      }
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
                  >
                    Ajouter 30 jours ({extensionPrice} {currency})
                  </button>
                </div>
                <p className="text-sm text-gray-400">
                  Votre publication sera visible pendant {formData.displayDuration} jours.
                  Vous pourrez prolonger l'affichage plus tard depuis votre profil.
                </p>
              </div>
            )}
            <p className="text-red-400 text-sm">
              Important : Une fois que votre publication aura reçu des dons, elle ne pourra plus être modifiée ou supprimée
            </p>
          </div>

          <button
            type="submit"
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-4 rounded-xl transition-colors"
          >
            Créer la publication
          </button>
        </form>
      </div>
    </div>
  );
}